import { useEffect, useState } from "react";
import {
  LabService,
  type DashboardAssignedAudit,
  type DashboardData as ServiceDashboardData,
  type Lab,
} from "../services/labService";
import { normalizeLabType } from "../types/lab";

export interface DashboardData {
  current_lab: Lab | null;
  all_labs: Lab[];
  assigned_audits: DashboardAssignedAudit[];
}

export interface UseDashboardResult {
  dashboardData: DashboardData;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useDashboard = (): UseDashboardResult => {
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    current_lab: null,
    all_labs: [],
    assigned_audits: [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      const data: ServiceDashboardData = await LabService.getDashboardData();
      setDashboardData({
        current_lab: data.current_lab
          ? {
              ...data.current_lab,
              type: normalizeLabType(data.current_lab.type),
            }
          : null,
        all_labs: data.all_labs.map((lab) => ({
          ...lab,
          type: normalizeLabType(lab.type),
        })),
        assigned_audits: data.assigned_audits.map((audit) => ({
          ...audit,
          lab_type: normalizeLabType(audit.lab_type),
        })),
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to fetch dashboard data";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchDashboardData();
  }, []);

  return {
    dashboardData,
    isLoading,
    error,
    refetch: fetchDashboardData
  };
};

export const GetDashboardDataFn = async () => {
  return await LabService.getDashboardData()
}

